import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Users, Building2, TrendingUp, Copy, Check,
  Download, CheckCircle, Clock, CreditCard,
  Landmark, ChevronDown, ChevronUp, Star,
  UserCheck, UserX, FileText, Award
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/utils/api';
import { toast } from 'sonner';

const fmt = (n) => (n || 0).toLocaleString('pt-PT');
const monthLabel = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' });

function PaymentModal({ amb, onClose, onSuccess }) {
  const [method, setMethod] = useState('vinti4');
  const [note, setNote]     = useState('');
  const [amount, setAmount] = useState(String(amb.earnings?.pending || ''));
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const value = parseInt(amount, 10);
  const valid = Number.isFinite(value) && value > 0;

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/ambassadors/${amb.user_id}/authorize-payment`, { method, note, amount: value, confirm: true });
      toast.success(`Pagamento de ${fmt(value)} ECV registado.`);
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao registar pagamento.');
    } finally {
      setSaving(false);
    }
  };

  const e = amb.earnings || {};
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={ev => ev.stopPropagation()}
        data-testid="payment-modal">
        <h3 className="text-lg font-bold text-gray-900 mb-1">Registar pagamento (manual)</h3>
        <p className="text-sm text-gray-500 mb-4">
          {amb.ambassador_name} · só regista que pagou por fora — nada é pago automaticamente.
        </p>

        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4 text-sm text-emerald-800 space-y-0.5" data-testid="payment-breakdown">
          <div className="flex justify-between"><span>Empresas activadas</span><strong>{fmt(e.fixed)} ECV</strong></div>
          <div className="flex justify-between"><span>Comissões</span><strong>{fmt(e.commissions)} ECV</strong></div>
          <div className="flex justify-between"><span>Bónus mensais</span><strong>{fmt(e.bonus_total)} ECV</strong></div>
          <div className="flex justify-between"><span>Já pago</span><strong>−{fmt(e.total_paid)} ECV</strong></div>
          <div className="flex justify-between border-t border-emerald-200 pt-1 mt-1 text-base"><span>Por pagar</span><strong data-testid="payment-pending">{fmt(e.pending)} ECV</strong></div>
        </div>

        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Valor pago (ECV)</p>
        <input type="number" min="1" value={amount} onChange={ev => { setAmount(ev.target.value); setConfirming(false); }}
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-blue-300"
          data-testid="payment-amount-input" />

        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Método de pagamento</p>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <button
            onClick={() => setMethod('vinti4')}
            className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all text-sm font-semibold
              ${method === 'vinti4' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
            data-testid="payment-method-vinti4">
            <CreditCard className="h-5 w-5" /> Vinti4
          </button>
          <button
            onClick={() => setMethod('transfer')}
            className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all text-sm font-semibold
              ${method === 'transfer' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
            data-testid="payment-method-transferencia">
            <Landmark className="h-5 w-5" /> Transferência
          </button>
        </div>

        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Nota interna (opcional)</p>
        <textarea
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-300 mb-5"
          rows={2} placeholder="Referência, data, observações…"
          value={note} onChange={ev => setNote(ev.target.value)}
          data-testid="payment-note-input"
        />

        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancelar</Button>
          {!confirming ? (
            <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setConfirming(true)} disabled={!valid}
              data-testid="review-payment-btn">
              Continuar
            </Button>
          ) : (
            <Button className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white" onClick={submit} disabled={saving || !valid}
              data-testid="confirm-payment-btn">
              {saving ? 'A guardar…' : `Confirmar ${fmt(value)} ECV`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function CompanyEarnings({ c }) {
  return (
    <div className="text-right text-[11px] text-gray-600 flex-shrink-0 w-44" data-testid="admin-amb-company-earnings">
      <div>Fixo: <strong>{fmt(c.fixed)}</strong>{c.status === 'activada' && c.scheme === 'legacy' ? ' (tabela antiga)' : ''}</div>
      <div>Comissões: <strong>{fmt(c.commission_total)}</strong>{c.commissions?.length ? ` (${c.commissions.length})` : ''}</div>
      {c.commissions?.map((l, i) => (
        <div key={i} className="text-[10px] text-gray-400">{new Date(l.at).toLocaleDateString('pt-PT')} · {fmt(l.amount)}{l.first_payment ? ' · 1.º pag.' : ''}</div>
      ))}
    </div>
  );
}

function AmbRow({ amb, onPaymentSuccess }) {
  const [expanded, setExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied]      = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(amb.code).then(() => {
      setCopied(true); toast.success('Copiado!');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const paymentPaid = amb.payment_status === 'authorized';
  const canPay = (amb.earnings?.pending || 0) > 0;

  return (
    <>
      <Card className="p-5" data-testid={`amb-row-${amb.code}`}>
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <TrendingUp className="h-5 w-5 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className="font-bold text-gray-900">{amb.ambassador_name}</span>
              <span className="text-gray-400 text-sm">{amb.ambassador_email}</span>
              <Badge className="font-mono text-xs bg-amber-100 text-amber-700 border-amber-200">{amb.code}</Badge>
              <button onClick={copy} className="text-gray-400 hover:text-gray-600">
                {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
              </button>
            </div>

            {/* Metrics row */}
            <div className="flex flex-wrap gap-3 text-sm mb-2">
              <span className="flex items-center gap-1 text-blue-600 font-semibold">
                <Building2 className="h-3.5 w-3.5" /> {amb.registrations} registos
              </span>
              <span className="flex items-center gap-1 text-emerald-600 font-semibold">
                <CheckCircle className="h-3.5 w-3.5" /> {amb.active_companies} activadas
              </span>
              {(amb.registrations - amb.active_companies) > 0 && (
                <span className="flex items-center gap-1 text-amber-500 font-semibold">
                  <Clock className="h-3.5 w-3.5" /> {amb.registrations - amb.active_companies} pendentes
                </span>
              )}
            </div>

            {/* Earnings */}
            <div className="flex flex-wrap items-center gap-2 text-xs" data-testid={`amb-earnings-${amb.code}`}>
              <Badge variant="outline" className="text-xs">Fixo {fmt(amb.earnings?.fixed)}</Badge>
              <Badge variant="outline" className="text-xs">Comissões {fmt(amb.earnings?.commissions)}</Badge>
              <Badge variant="outline" className="text-xs">Bónus {fmt(amb.earnings?.bonus_total)}</Badge>
              <span className="font-bold text-gray-700">Pago {fmt(amb.earnings?.total_paid)}</span>
              <Badge className={`text-xs ${canPay ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}`}
                data-testid={`amb-pending-${amb.code}`}>
                Por pagar {fmt(amb.earnings?.pending)} ECV
              </Badge>
            </div>
            {amb.earnings?.bonuses?.filter(b => b.amount > 0).length > 0 && (
              <div className="mt-1 text-[11px] text-indigo-600">
                Bónus: {amb.earnings.bonuses.filter(b => b.amount > 0).map(b => `${monthLabel(b.month)} (${b.activated} activadas)`).join(' · ')}
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex flex-col gap-2 flex-shrink-0">
            {canPay && (
              <Button size="sm" onClick={() => setShowModal(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1.5"
                data-testid={`authorize-payment-btn-${amb.code}`}>
                <CreditCard className="h-3.5 w-3.5" /> Registar pagamento
              </Button>
            )}
            {amb.registrations > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setExpanded(v => !v)}
                className="text-gray-500 text-xs gap-1">
                {expanded ? <><ChevronUp className="h-3.5 w-3.5" /> Ocultar</> : <><ChevronDown className="h-3.5 w-3.5" /> Ver empresas</>}
              </Button>
            )}
          </div>
        </div>

        {/* Payment note */}
        {paymentPaid && amb.payment_note && (
          <div className="mt-3 bg-emerald-50 rounded-lg px-3 py-2 text-xs text-emerald-700 border border-emerald-100">
            {amb.payment_note}
          </div>
        )}

        {/* Company list */}
        {expanded && amb.companies?.length > 0 && (
          <div className="mt-4 border-t pt-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Empresas registadas com {amb.code}
            </p>
            <div className="space-y-1">
              {amb.companies.map((c, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold
                    ${c.status === 'activada' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-50 text-amber-600'}`}>
                    {(c.company_name || c.email || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-800 truncate">{c.company_name || '—'}</div>
                    <div className="flex gap-2 mt-0.5">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full
                        ${c.has_nif ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                        NIF {c.has_nif ? '✓' : '—'}
                      </span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full
                        ${c.has_job ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}>
                        Vaga {c.has_job ? '✓' : '—'}
                      </span>
                    </div>
                  </div>
                  <div className={`text-xs font-bold flex-shrink-0
                    ${c.status === 'activada' ? 'text-emerald-600' : 'text-amber-500'}`}>
                    {c.status === 'activada' ? 'Activada' : 'Pendente'}
                  </div>
                  <div className="text-xs text-gray-400 flex-shrink-0">
                    {c.registered_at ? new Date(c.registered_at).toLocaleDateString('pt-PT') : '—'}
                  </div>
                  <CompanyEarnings c={c} />
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {showModal && (
        <PaymentModal amb={amb} onClose={() => setShowModal(false)} onSuccess={onPaymentSuccess} />
      )}
    </>
  );
}

export default function AdminAmbassadors() {
  const navigate = useNavigate();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [applications, setApplications] = useState([]);
  const [appsLoading, setAppsLoading]   = useState(true);
  const [ambassadorJobId, setAmbassadorJobId] = useState(null);
  const [approvingId, setApprovingId]   = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/admin/ambassadors')
      .then(r => setData(r.data))
      .catch(() => toast.error('Erro ao carregar embaixadores'))
      .finally(() => setLoading(false));
  };

  // Carregar candidaturas à vaga de embaixador
  const loadApplications = async () => {
    setAppsLoading(true);
    try {
      // Encontrar vaga is_ambassador_job
      const jobsResp = await api.get('/admin/jobs?limit=100');
      const jobs = jobsResp.data?.jobs || jobsResp.data || [];
      const ambJob = jobs.find(j => j.is_ambassador_job || (j.title_pt || '').toLowerCase().includes('embaixa'));
      if (ambJob) {
        setAmbassadorJobId(ambJob.id);
        const appsResp = await api.get(`/applications/job/${ambJob.id}`);
        setApplications(Array.isArray(appsResp.data) ? appsResp.data : []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setAppsLoading(false);
    }
  };

  useEffect(() => { load(); loadApplications(); }, []);

  const approveAmbassador = async (applicationId, candidateName) => {
    setApprovingId(applicationId);
    try {
      const r = await api.post(`/admin/ambassador/approve/${applicationId}`);
      toast.success(`${candidateName} aprovado(a)! Código: ${r.data.code}`);
      load(); loadApplications();
    } catch {
      toast.error('Erro ao aprovar embaixador.');
    } finally {
      setApprovingId(null);
    }
  };

  const exportCSV = () => {
    if (!data?.ambassadors) return;
    const rows = [['Código', 'Nome', 'Email', 'Registos', 'Activadas', 'ECV Fixo', 'ECV Comissões', 'ECV Bónus', 'ECV Total', 'ECV Pago', 'ECV Por pagar']];
    data.ambassadors.forEach(a => rows.push([
      a.code, a.ambassador_name, a.ambassador_email,
      a.registrations, a.active_companies,
      a.earnings?.fixed || 0, a.earnings?.commissions || 0, a.earnings?.bonus_total || 0,
      a.earnings?.total_earned || 0, a.earnings?.total_paid || 0, a.earnings?.pending || 0,
    ]));
    const csv  = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href = url; a.download = 'embaixadores.csv'; a.click();
  };

  const downloadPromoStory = async () => {
    try {
      const token = localStorage.getItem('trabadju_access_token');
      const resp  = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/promo/ambassador-story`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = 'Trabadju_Embaixadores_Story.png'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Imagem Story descarregada!');
    } catch {
      toast.error('Erro ao gerar imagem.');
    }
  };

  const totalActive   = data?.ambassadors.reduce((s, a) => s + a.active_companies, 0) || 0;
  const totalEarnings = data?.ambassadors.reduce((s, a) => s + (a.earnings?.pending || 0), 0) || 0;
  const pendingPay    = data?.ambassadors.filter(a => (a.earnings?.pending || 0) > 0).length || 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">Programa Embaixadores</h1>
            <p className="text-sm text-gray-500">Ganhos, comissões e registo manual de pagamentos</p>
          </div>
          <Button variant="outline" onClick={exportCSV} className="gap-2">
            <Download className="h-4 w-4" /> Exportar CSV
          </Button>
          <Button onClick={downloadPromoStory} className="gap-2 bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="download-promo-story-btn">
            <Star className="h-4 w-4" /> Story Promo
          </Button>
        </div>

        {loading && <div className="text-center py-16 text-gray-400">A carregar…</div>}

        {data && (
          <>
            {/* Totais */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <Card className="p-4 text-center">
                <div className="text-3xl font-black text-amber-600">{data.ambassadors.length}</div>
                <div className="text-xs text-gray-500 mt-1">Embaixadores</div>
              </Card>
              <Card className="p-4 text-center">
                <div className="text-3xl font-black text-blue-600">
                  {data.ambassadors.reduce((s, a) => s + a.registrations, 0)}
                </div>
                <div className="text-xs text-gray-500 mt-1">Registos</div>
              </Card>
              <Card className="p-4 text-center">
                <div className="text-3xl font-black text-emerald-600">{totalActive}</div>
                <div className="text-xs text-gray-500 mt-1">Activadas</div>
              </Card>
              <Card className="p-4 text-center">
                <div className="text-lg font-black text-violet-700">{totalEarnings.toLocaleString('pt-PT')}</div>
                <div className="text-xs text-gray-500 mt-1">ECV a pagar</div>
                {pendingPay > 0 && (
                  <div className="text-[10px] font-semibold text-amber-600 mt-0.5">{pendingPay} aguardam pagamento</div>
                )}
              </Card>
            </div>

            {/* Tabela de pagamento (referência) */}
            <Card className="p-4 mb-6" data-testid="admin-amb-rules">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Tabela de pagamento (desde 30/10/2026)</p>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div><div className="text-base font-black text-gray-800">600 ECV</div><div className="text-xs text-gray-500">por empresa activada</div></div>
                <div><div className="text-base font-black text-gray-800">30%</div><div className="text-xs text-gray-500">pagamentos nos primeiros 90 dias · mín. 500 no 1.º</div></div>
                <div><div className="text-base font-black text-gray-800">2.000 ECV</div><div className="text-xs text-gray-500">bónus/mês com 10 activadas</div></div>
              </div>
              <p className="text-[11px] text-gray-400 mt-2 text-center">Empresas activadas antes de 30/10/2026 contam 300 ECV. Pagamentos sempre registados à mão.</p>
            </Card>

            {/* === CANDIDATURAS À VAGA DE EMBAIXADOR === */}
            <Card className="p-5 mb-6 border-blue-200 bg-blue-50">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="font-bold text-blue-900">Candidaturas ao Programa Embaixadores</h2>
                  <p className="text-xs text-blue-600 mt-0.5">Selecciona os candidatos após entrevista · limite: 20 candidaturas</p>
                </div>
                {ambassadorJobId && (
                  <Button variant="outline" size="sm" onClick={() => navigate(`/admin/jobs/${ambassadorJobId}/applications`)}
                    className="border-blue-300 text-blue-700 hover:bg-blue-100 text-xs">
                    Ver todas
                  </Button>
                )}
              </div>

              {appsLoading && <div className="text-center py-4 text-blue-400 text-sm">A carregar…</div>}

              {!appsLoading && applications.length === 0 && (
                <p className="text-center text-blue-400 text-sm py-4">
                  {ambassadorJobId ? 'Sem candidaturas ainda.' : 'Nenhuma vaga de Embaixador encontrada. Cria uma vaga com "is_ambassador_job" activo.'}
                </p>
              )}

              {!appsLoading && applications.length > 0 && (
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-blue-600 font-semibold px-1 mb-2">
                    <span>{applications.length} candidatura{applications.length !== 1 ? 's' : ''}</span>
                    <span>{applications.filter(a => a.status === 'ambassador_approved').length} aprovado{applications.filter(a => a.status === 'ambassador_approved').length !== 1 ? 's' : ''}</span>
                  </div>
                  {applications.map(app => {
                    const approved = app.status === 'ambassador_approved';
                    return (
                      <div key={app.id}
                        className={`flex items-center gap-3 p-3 rounded-xl border ${approved ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-100'}`}
                        data-testid={`application-row-${app.id}`}>
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0
                          ${approved ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                          {(app.candidate_name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900">{app.candidate_name}</div>
                          <div className="text-xs text-gray-400">{app.candidate_email} · {app.applied_at ? new Date(app.applied_at).toLocaleDateString('pt-PT') : ''}</div>
                          {approved && app.ambassador_code && (
                            <div className="text-xs text-emerald-600 font-mono font-bold mt-0.5">Código: {app.ambassador_code}</div>
                          )}
                        </div>
                        {approved ? (
                          <div className="flex items-center gap-1 text-xs font-bold text-emerald-600 flex-shrink-0">
                            <Award className="h-4 w-4" /> Embaixador
                          </div>
                        ) : (
                          <Button size="sm"
                            onClick={() => approveAmbassador(app.id, app.candidate_name)}
                            disabled={approvingId === app.id}
                            className="bg-amber-500 hover:bg-amber-600 text-white text-xs gap-1.5 flex-shrink-0"
                            data-testid={`approve-btn-${app.id}`}>
                            <UserCheck className="h-3.5 w-3.5" />
                            {approvingId === app.id ? 'A aprovar…' : 'Aprovar'}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* === EMBAIXADORES ACTIVOS === */}
            {/* === EMBAIXADORES ACTIVOS === */}
            {data.ambassadors.length === 0 && (
              <Card className="p-12 text-center text-gray-400">
                <Users className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p className="font-medium">Nenhum embaixador ainda</p>
              </Card>
            )}

            <div className="space-y-4">
              {data.ambassadors.map(amb => (
                <AmbRow key={amb.code} amb={amb} onPaymentSuccess={load} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
